import React, { useEffect, useRef, useState } from 'react';
import CreateQuestions from '../../../component/EliteGame/CreateQuestions';
import QuestionsList from '../../../component/EliteGame/QuestionsList';
import {
  createQuestion,
  GetMainCategories,
  GetSubCategories,
  getQuestionsByQuizType,
  updateQuestion,
  deleteQuestion
} from '../../../api/eliteGameApi';
import { ELITE_QUIZ_TYPE } from '../../../utils/constants';
import { useEliteGames } from '../../../context/EliteGamesContext';
import ViewQuestionModal from '../../../component/EliteGame/ViewQuestionModal';
import { errorToast, successToast } from '../../../utils/helper';
import DeletePopup from '../../../component/common/modals/DeletePopup';

const MathsQuizManageQuestions = () => {
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [refresh, setRefresh] = useState(true);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [refreshFlag, setRefreshFlag] = useState(0);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewQuestion, setViewQuestion] = useState(null);
  const [questionId, setQuestionId] = useState(null);
  const [open, setOpen] = useState(false);
  const { setLoading } = useEliteGames();
  const formRef = useRef(null);

  // Function to handle viewing a question
  const handleView = (question) => {
    setViewQuestion(question);
    setViewModalOpen(true);
  };

  const getMainCategories = async () => {
    setLoading(true);
    try {
      const data = await GetMainCategories(
        { quizType: ELITE_QUIZ_TYPE.MATH_QUIZ, limit: 50 },
        true
      );
      console.log('Fetched main categories:', data);
      setMainCategories(data.data || []);
    } catch (error) {
      console.error('Failed to fetch main categories', error);
    } finally {
      setLoading(false);
    }
  };

  const getQuestions = async (filter = {}) => {
    setLoading(true);
    try {
      const data = await getQuestionsByQuizType(ELITE_QUIZ_TYPE.MATH_QUIZ, {
        page,
        limit,
        ...filter
      });
      console.log('Fetched questions:', data);
      setQuestions(data || []);
    } catch (error) {
      errorToast('Failed to fetch questions', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getMainCategories();
  }, []);

  useEffect(() => {
    getQuestions();
  }, [refresh, page, limit]);

  const getSubCategories = async (mainCategoryId) => {
    setLoading(true);
    try {
      const data = await GetSubCategories(
        { categoryId: mainCategoryId, quizType: ELITE_QUIZ_TYPE.MATH_QUIZ, limit: 50 },
        true
      );
      setSubCategories(data.data || []);
    } catch (error) {
      console.error('Failed to fetch sub categories', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (question) => {
    setSelectedQuestion(question);
    formRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const formData = {
        ...data,

        quizType: ELITE_QUIZ_TYPE.MATH_QUIZ,
        options: [
          data.optionA,
          data.optionB,
          ...(data.questionType != 2 ? [data.optionC, data.optionD] : [])
        ],
        correctAnswer: data[data.answer?.value || data.answer]
      };
      console.log('Submitted Data:', formData);
      if (selectedQuestion) {
        const response = await updateQuestion(formData, selectedQuestion._id);
        successToast('Question updated successfully:', response.message);
      } else {
        const response = await createQuestion(formData);
        successToast('Question created successfully:', response.message);
        setRefreshFlag((prev) => prev + 1);
      }
      getQuestions(); // Refresh questions list after creating a new question
      setRefresh(true); // Refresh questions list after creating a new question
      setSelectedQuestion(null); // Clear selected question after submission
    } catch (error) {
      errorToast('Error creating/updating question:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async () => {
    setLoading(true);
    try {
      // Call API to delete question
      await deleteQuestion(questionId);
      successToast('Question deleted successfully');
      setRefresh(true); // Refresh questions list after deletion
    } catch (error) {
      errorToast('Error deleting question:', error);
    } finally {
      setLoading(false);
    }
    setRefresh(!refresh);
  };
  const handleFilter = (filter) => {
    getQuestions(filter);
  };

  // Handle refresh action for questions
  const handleRefresh = () => {
    // Reset page to 1 and refresh questions
    setPage(1);
    getQuestions(); // Fetch fresh data
  };

  return (
    <div>
      <div ref={formRef}>
        <CreateQuestions
          onSubmit={onSubmit}
          name={'Maths Quiz'}
          quizType={ELITE_QUIZ_TYPE.MATH_QUIZ}
          mainCategories={mainCategories}
          getSubCategories={getSubCategories}
          subCategories={subCategories}
          existingQuestion={selectedQuestion}
          resetTrigger={refreshFlag}
        />
      </div>
      <div className="bg-white rounded-lg shadow p-6 m-4">
        <div className="mb-2">
          <span className="text-2xl font-bold text-black-600">Questions for Maths Quiz</span>
        </div>
        <hr className="mb-6" />
        <QuestionsList
          quizType={ELITE_QUIZ_TYPE.MATH_QUIZ}
          questions={questions}
          categoryList={mainCategories}
          subCategoryList={subCategories}
          getSubCategories={getSubCategories}
          onEdit={handleEdit}
          page={page}
          limit={limit}
          setLimit={setLimit}
          setPage={setPage}
          onView={handleView}
          onDelete={(id) => {
            setQuestionId(id);
            setOpen(true);
          }}
          onFilter={handleFilter}
          onRefresh={handleRefresh}
        />
        {DeletePopup({
          title: 'Confirm Deletion',
          open,
          setOpen,
          message:
            'Are you sure you want to delete this question? All related data will also be deleted.',
          setDelete: handleDelete
        })}
      </div>
      <ViewQuestionModal
        questions={viewQuestion}
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
      />
    </div>
  );
};

export default MathsQuizManageQuestions;
